/**
 * useAficionVisitanteTheater — predicado de elegibilidad del modal "Afición visitante".
 *
 * Centraliza la regla que decide si el modal informativo de afición visitante
 * debe mostrarse. El PR anterior dejaba el hash literal dentro de la vista de
 * compra, mezclando política de negocio con infraestructura del modal. Esta
 * ubicación única permite que vistas, scripts y tests compartan una sola fuente
 * de verdad y que el magic string nunca se filtre a los consumidores.
 *
 * Diseño:
 *   - `AFICION_VISITANTE_TEATRO_HASH` se exporta como constante para que los
 *     tests y otros módulos puedan afirmar contra la fuente de verdad sin
 *     hardcodear el hash de nuevo.
 *   - `AFICION_VISITANTE_MODAL_NAME` se exporta para evitar repetir el nombre
 *     del modal cada vez que la vista lo abre o lo cierra.
 *   - `isAficionVisitanteTheater(teatroHash)` es un predicado puro: recibe el
 *     hash del teatro y devuelve un booleano. Esto permite usarlo tanto desde
 *     Options API como desde Composition API sin depender de `getCurrentInstance`.
 */

/**
 * Hash del teatro para el cual aplica el aviso de afición visitante.
 * @type {string}
 */
export const AFICION_VISITANTE_TEATRO_HASH = 'PSaG3BjUboDrGdZz6SQq';

/**
 * Nombre del modal en vue-js-modal para abrir/cerrar el aviso.
 * @type {string}
 */
/**
 * CE Sabadell: no avisa de "afición visitante" en general, sino de que no se
 * puede acceder fuera de la **zona visitante** con simbología del equipo.
 * @type {string}
 */
export const AFICION_VISITANTE_ZONA_TEATRO_HASH = 'BHlBbABzmZ5qkeNOHcGH';

/**
 * Mapa hash del teatro → clave i18n del cuerpo del aviso. Es la fuente única del
 * texto: cada teatro con aviso propio apunta a su clave y el resto usa la genérica.
 */
const AFICION_VISITANTE_CUERPO_KEYS = {
    [AFICION_VISITANTE_TEATRO_HASH]: 'aficion_visitante.cuerpo',
    [AFICION_VISITANTE_ZONA_TEATRO_HASH]: 'aficion_visitante.cuerpo_zona_visitante'
};

export const AFICION_VISITANTE_MODAL_NAME = 'aficion-visitante';

/**
 * Predicado puro que decide si el modal aplica para un teatro dado.
 *
 * @param {string|null|undefined} teatroHash - hash del teatro actual
 * @returns {boolean} true cuando el teatro es el objetivo del aviso
 */
export function isAficionVisitanteTheater(teatroHash) {
    // Dos teatros con aviso de afición visitante: uno con el texto general y otro
    // con el de "no se puede acceder fuera de la zona visitante".
    return teatroHash === AFICION_VISITANTE_TEATRO_HASH
        || teatroHash === AFICION_VISITANTE_ZONA_TEATRO_HASH;
}

/**
 * Clave i18n del cuerpo del aviso para el teatro dado, con la genérica de respaldo.
 *
 * @param {string} teatroHash
 * @returns {string}
 */
export function getAficionVisitanteCuerpoKey(teatroHash) {
    return AFICION_VISITANTE_CUERPO_KEYS[teatroHash] || 'aficion_visitante.cuerpo';
}
